// Pure helpers for the workbench page. No React, no DOM -- `node --test`able.
// Event and approval shapes are app/workbench/operator.py's (EVENT_KINDS and
// _approval_dict); a payload that failed to parse arrives as its raw string.

import { PRIORITY_RESERVED } from './priority-reserved.mjs';

// Which run this viewer had open, per company. A per-viewer convenience, so it
// is browser storage and every access is guarded: a blocked store only costs
// the reopen, never the page. It lives HERE because chat.jsx's task panel
// writes it to deep-link into this page (Task 12 review M7) -- two literals
// would let a rename break that link with nothing failing.
export const RUN_KEY = 'pcm_wb_run';

// The env both the workbench and the Studio open on, per company (studio follow-up spec §4.2):
// the remembered one if it still exists, else the env of the company's newest run, else the first.
// Every storage access is wrapped: a private window or blocked site data throws.
// The run this viewer had open in one company (picker, spec §4.1) -- separate from RUN_KEY's single object.
export const RUN_ENV_KEY_PREFIX = 'pcm_wb_run:';
export function rememberedRunFor(tenantId, env) {
  try {
    const n = Number(localStorage.getItem(`${RUN_ENV_KEY_PREFIX}${tenantId}:${env}`));
    return Number.isInteger(n) && n > 0 ? n : null;
  } catch (_) { return null; }
}
export function rememberRunFor(tenantId, env, runId) {
  try { localStorage.setItem(`${RUN_ENV_KEY_PREFIX}${tenantId}:${env}`, String(runId)); } catch (_) { /* no store */ }
}
// POST /workbench/runs's body. dump_key only with the offline pick's key (picker spec §4.5), so a
// connected tenant's request is byte for byte what it was.
export const runCreateBody = ({ tenantId, env, title, dumpKey, ...rest }) =>
  ({ tenant_id: tenantId, env, title, ...rest, ...(dumpKey != null ? { dump_key: dumpKey } : {}) });

export const ENV_KEY_PREFIX = 'pcm_env:';
export function rememberedEnv(tenantId) {
  try { return localStorage.getItem(`${ENV_KEY_PREFIX}${tenantId}`) || null; } catch (_) { return null; }
}
export function rememberEnv(tenantId, env) {
  try { localStorage.setItem(`${ENV_KEY_PREFIX}${tenantId}`, env); } catch (_) { /* no store */ }
}
export function pickEnv({ remembered, envs, newestRunEnv } = {}) {
  const list = envs || [];
  if (remembered && list.includes(remembered)) return remembered;
  if (newestRunEnv && list.includes(newestRunEnv)) return newestRunEnv;
  return list[0] || '';
}

// Offline installations (spec 2026-10-14-offline-installations §3.7). A company of the offline
// tenant is a customer's Priority the agent cannot reach: everything its runs hand out runs
// through the person on this page, so the picker says so. A developer's contexts row carries
// `offline_envs`; an admin reads the offline tenant's companies from the offline overview.
export const OFFLINE_ENV_LABEL = { en: 'offline -- runs through you', he: 'לא מחוברת -- רצה דרכך' };
export function offlineEnvs({ tenant = null, overview = null } = {}) {
  if (!tenant) return [];
  if (Array.isArray(tenant.offline_envs)) return [...tenant.offline_envs];
  if (tenant.offline && overview && overview.tenant && overview.tenant.id === tenant.id) {
    return (overview.companies || []).map((c) => c.env);
  }
  return [];
}
export function mergeEnvs(envs, offline) {
  return [...new Set([...(envs || []), ...(offline || [])])];
}
export function envLabel(env, offline, label) {
  return (offline || []).includes(env) ? `${env} — ${label}` : env;
}

export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;   // operator.MAX_UPLOAD_BYTES
export const REPLY_MAX = 8000;                      // DecisionBody.reply, ReplyBody.text
export const FAIL_REASON_MAX = 1000;                // FailBody.reason

// runs.STOP_TITLE, mirrored here as the one place in the JS that names it
// (Minor 1, whole-branch review of PR 1) -- everything that needs to spot the
// stop card reads it from here, never a second copy of the literal.
export const STOP_TITLE = 'stuck: stopped by the operator';

const obj = (p) => (p && typeof p === 'object' && !Array.isArray(p) ? p : {});
const textOf = (p) => (typeof p === 'string' ? p : String(obj(p).text ?? ''));

export function costText(payload) {
  const p = obj(payload);
  // The interrupt fallback has no cost (runner.record_result): an unknown
  // leg is never rendered as $0, which would read as "this leg was free".
  if (p.cost_unknown === true) return 'cost unknown for this leg';
  // CLI 2.1.277+ reports a resumed session's CUMULATIVE total in
  // total_cost_usd; leg_cost_usd is this leg's own share. Older events have
  // only total_cost_usd, which CLI 2.1.259 reported per leg.
  if (typeof p.leg_cost_usd === 'number') return `$${p.leg_cost_usd.toFixed(4)}`;
  if (typeof p.total_cost_usd === 'number') return `$${p.total_cost_usd.toFixed(4)}`;
  return null;
}

// A card pauses the leg (night shift spec §5.1): the SDK denies the gated call -- a tool_result
// stored with is_error true -- and ends the leg with a `result` that is `paused` and names the call
// in `permission_denials`. That is a wait for a person, not a failure. The SDK's fixed denial text
// is the fallback for a poll that delivers the tool_result before its `result` event.
export const PAUSE_DENIAL = "The user doesn't want to proceed with this tool use";
export function pausedToolIds(events) {
  const ids = new Set();
  for (const ev of events || []) {
    const p = obj(ev.payload);
    if (ev.kind !== 'result' || p.paused !== true || !Array.isArray(p.permission_denials)) continue;
    for (const d of p.permission_denials) if (obj(d).tool_use_id != null) ids.add(obj(d).tool_use_id);
  }
  return ids;
}
export function isPauseDenial(payload, ids) {
  const p = obj(payload);
  if (p.is_error !== true) return false;
  if (p.tool_use_id != null && ids && ids.has(p.tool_use_id)) return true;
  return typeof p.content === 'string' && p.content.startsWith(PAUSE_DENIAL);
}

// One render block per run of events. Consecutive `text` events become one
// block; a `tool_result` joins its `tool_use` by id; an unknown kind is
// skipped rather than breaking the page when the runner learns a new one.
export function groupEvents(events) {
  const out = [];
  const tools = new Map();
  const paused = pausedToolIds(events);
  for (const ev of events || []) {
    const p = ev.payload;
    const last = out[out.length - 1];
    switch (ev.kind) {
      case 'text':
        if (last && last.type === 'text') last.text = `${last.text}\n\n${textOf(p)}`;
        else out.push({ type: 'text', seq: ev.seq, text: textOf(p) });
        break;
      case 'tool_use': {
        const block = { type: 'tool', seq: ev.seq, id: obj(p).id ?? null,
                        name: obj(p).name ?? null, input: obj(p).input ?? null, result: null };
        if (block.id != null) tools.set(block.id, block);
        out.push(block);
        break;
      }
      case 'tool_result': {
        const result = { is_error: obj(p).is_error === true, content: obj(p).content ?? '',
                         paused: isPauseDenial(p, paused) };
        const owner = tools.get(obj(p).tool_use_id);
        if (owner) owner.result = result;
        else out.push({ type: 'tool', seq: ev.seq, id: null, name: null, input: null, result });
        break;
      }
      case 'prompt':
        out.push({ type: 'prompt', seq: ev.seq, text: textOf(p) });
        break;
      case 'notice':
        out.push({ type: 'notice', seq: ev.seq, text: textOf(p) });
        break;
      case 'result': {
        const r = obj(p);
        out.push({ type: 'result', seq: ev.seq, subtype: r.subtype ?? null,
                   terminal_reason: r.terminal_reason ?? null, is_error: r.is_error === true,
                   paused: r.paused === true, turns: r.num_turns ?? null, cost: costText(r) });
        break;
      }
      case 'promoted':
        out.push({ type: 'line', seq: ev.seq, text: obj(p).concept
          ? `lesson learned/${obj(p).concept}.md promoted`
          : `skill ${obj(p).skill} promoted` });  // recorded before lessons (review L8)
        break;
      case 'upload_delivered':
        out.push({ type: 'line', seq: ev.seq,
                   text: `file ${obj(p).filename} delivered${obj(p).already_present ? ' (already present)' : ''}` });
        break;
      case 'operator_reply':
        out.push({ type: 'reply', seq: ev.seq, text: textOf(p), by: obj(p).by ?? null });
        break;
      // The app ended this run's autonomy because an auto-approved card's job
      // failed on the ERP (app/workbench/autodecide.py). Rendered, or the one
      // thing an operator must notice about an unattended run is fetched by
      // this page and silently dropped by `default:` below.
      case 'controller_control':
      case 'controller_control_ack':
        if (textOf(p)) out.push({ type: 'notice', seq: ev.seq, text: textOf(p) });
        break;
      case 'autonomy_ended':
        out.push({ type: 'line', seq: ev.seq,
                   text: `autonomy ended — ${obj(p).reason || 'an auto-approved card failed'}; approve a fresh plan to grant it again` });
        break;
      // The skill router's result (app/skillrouter, spec 2026-09-27 §8). Only
      // a timeout or an error renders: it is the one sign on the page that a
      // dead key or a slow model left this run without its required reading.
      // An `ok` one is in leg one's prompt; disabled / skipped / no_lanes
      // would be noise on every dormant run.
      case 'router_context': {
        // An offline run's event is always `ok` (plan D6: the offline brief is in it); the
        // router's own answer is `router_status`, and a dead router must still show.
        const pl = obj(p);
        const status = pl.offline ? pl.router_status : pl.status;
        if (status === 'timeout' || status === 'error') {
          out.push({ type: 'line', seq: ev.seq,
                     text: pl.offline
                       ? `skill router: ${status} — only the offline brief was added to this prompt`
                       : `skill router: ${status} — no required reading was added to this prompt` });
        }
        break;
      }
      default:
        break;
    }
  }
  return out;
}

const LABELS = {
  send_mail: 'Mail to send',
  hand_me_dbi: 'Paste this into WINDBI, then tell the agent what happened',
  run_sqli: 'Approve to run this SQLI once on the ERP',
  enqueue_write_job: 'Approve to run this Form Load job — creates real documents',
  promote_note: 'Promote this note to a verified lesson, indexed for every run',
  stuck: 'The agent is stuck — one sentence usually unblocks it',
  wait: 'Sleeping — it wakes on its own at the time below; wake it now to continue sooner',
  plan: 'Continue — approving this plan grants the run autonomy for the objects it names',
  dispatch: 'Start this stage of the plan as its own run',
  upgrade_sh: 'Review this upgrade file — approval adds its install file to Files',
  record_job: 'Record this delivered job — whoever changes these objects next reads it first',
  read_back_testbed: 'Approve to run this read-only SELECT once on the test bed this card names',
};

// One line per object a record_job card says it changed (spec 2026-09-28
// §B.7): the body is the agent's; malformed entries are shown, not dropped.
export function recordEntityLines(record) {
  const list = record && Array.isArray(record.entities) ? record.entities : [];
  return list.map((e) => {
    const o = obj(e);
    const part = o.column ? `.${o.column}` : o.trigger ? `/${o.trigger}` : '';
    return `${o.ename || '?'}${part} (${o.type || '?'}) — ${o.state || '?'}`;
  });
}

// Offline installations B7 (review M4): where a test-bed card acts, from the ROW's target
// columns (operator._approval_dict) -- never from the title, which only repeats it.
export function testBedTarget(approval) {
  const a = obj(approval);
  if (typeof a.target_env !== 'string' || !a.target_env) return null;
  return `${a.target_tenant_id}:${a.target_env}`;
}

// From the KIND, never the title: the title is agent-authored (review M9).
export function approvalLabel(approval) {
  const kind = obj(approval).kind;
  // An ordinary read_back is automatic, never a card; a test-bed one waits for a person (D12).
  if (kind === 'read_back') return obj(approval).target_env ? LABELS.read_back_testbed : null;
  return LABELS[kind] || `Unrecognised approval kind: ${kind}`;
}

// `actionable` is the server's (operator._approval_dict): a pending approval
// on a run that is not waiting is inert, so it is never re-derived here.
// `decided` (anything with `.has(id)`) holds the approvals this page has
// just decided: they leave at once, without waiting on a poll that can fail.
export function cardsFor(approvals, decided) {
  return (approvals || []).filter((a) => a.actionable === true
    && (a.kind !== 'read_back' || a.target_env)
    && !(decided && decided.has(a.id)));
}

// The server takes an absent reply for every kind; for these two the reply
// IS the content the agent resumes with, so the page is the only gate.
// The stop card is the exception: its one button is Continue, and a reply is only
// a correction (run 262: a required reply read as "say stop to stay stopped").
export function replyRequired(kind, title) {
  return kind === 'hand_me_dbi' || (kind === 'stuck' && title !== STOP_TITLE);
}

// A `wait` card is decided only one way: waking it. Rejecting a sleep means
// nothing the agent could act on -- cancel the run instead.
export function wakeOnly(kind) {
  return kind === 'wait';
}

// Minor 1 (whole-branch review of PR 1): the stop card's Reject does exactly
// what Approve does (operator.py's decide-time sweep reads `kind == "stuck"
// and title == runs.STOP_TITLE`, not `approved`) -- both branches sweep,
// re-queue and tell the agent to follow the reply. The card's own body
// already says "reply ... or cancel the run", so Reject is offered with
// nothing distinct for it to mean. Keyed on kind AND title, unlike
// `wakeOnly`: `stuck` is also the kind of every ordinary stuck/ceiling card,
// which keeps its Reject.
export function isStopCard(approval) {
  const a = obj(approval);
  return a.kind === 'stuck' && a.title === STOP_TITLE;
}

export function decisionProblem(kind, reply, title) {
  const r = reply || '';
  if (r.length > REPLY_MAX) return `A reply is at most ${REPLY_MAX} characters.`;
  if (replyRequired(kind, title) && !r.trim()) return 'A reply is required: it is what the agent reads next.';
  return null;
}

const STUCK = { no_progress: 'stuck: no progress', budget: 'stuck: turn budget nearly spent' };

// `stuck` is derived by the server (runs.is_stuck), which flags only a
// running run. A waiting run is the design working -- a person has a card --
// and a run that is over has nothing left to unstick; the page holds the
// same rule, so a row read before a fail or cancel cannot keep the badge.
export function stuckBadge(run) {
  if (!run || !run.stuck || run.status !== 'running') return null;
  return STUCK[run.stuck] || `stuck: ${run.stuck}`;
}

export function errorText(err) {
  if (!err) return '';
  const d = err.detail;
  if (err.status === 503 && d && d.reason === 'run_busy') return 'the run is busy, try again in 30 s';
  if (d && typeof d === 'object' && !Array.isArray(d) && d.message) return String(d.message);
  if (typeof d === 'string' && d) return d;
  if (Array.isArray(d) && d.length) return d.map((e) => (e && e.msg) || String(e)).join('; ');
  return String(err.message || err);
}

// RB71: invisible direction/format controls. The browser OBEYS them in a
// text child (XSS-safe is not display-safe), so an agent-authored body can
// read on screen in a different order from what is pasted into WINDBI or
// run. The set is the controller's ruling: ALM, ZWSP/ZWNJ/ZWJ/LRM/RLM, the
// LRE..RLO embeddings and overrides, WJ, the LRI..PDI isolates, and BOM --
// plus DEL (RB84), which nothing gates in transcript or note text. No other
// C0 character: CR ends every line of CRLF Priority output.
const CONTROLS = /([\u007F\u061C\u200B-\u200F\u202A-\u202E\u2060\u2066-\u2069\uFEFF])/;

const controlLabel = (cp) => `⟨U+${cp.toString(16).toUpperCase().padStart(4, '0')}⟩`;

// {segments, count}: plain text and controls in order, each control carrying
// the visible label the page renders in its place. Never an empty list.
export function revealControls(text) {
  const parts = String(text ?? '').split(CONTROLS);   // odd indexes are the captured controls
  const segments = [];
  let count = 0;
  parts.forEach((part, i) => {
    if (i % 2 === 1) {
      const code = part.codePointAt(0);
      segments.push({ type: 'control', code, label: controlLabel(code) });
      count += 1;
    } else if (part) {
      segments.push({ type: 'text', text: part });
    }
  });
  if (segments.length === 0) segments.push({ type: 'text', text: '' });
  return { segments, count };
}

// app/workbench/devruns.py's DELETE_RE and STAGING_RE, the same in meaning:
// a DELETE the trusted lane holds is exactly a DELETE this highlights (spec
// 2026-09-24-mail-dev-runs §3.5). No `u` flag, so \b and /i are ASCII-only,
// as re.ASCII makes them there; whitespace is spelled out because \s here
// also matches NBSP. tests/fixtures/delete_cases.json pins both sides.
const DELETE_RE = /\bDELETE\b/gi;
const STAGING_RE = /DELETE[ \t\r\n]+FROM[ \t\r\n]+(?:NCATALOG|NCOLUMNS|NINDEXES|NINDCLMNS)[ \t\r\n]+WHERE[ \t\r\n]+USER[ \t\r\n]*=[ \t\r\n]*SQL\.USER[ \t\r\n]*;/iy;

// The tokens on which Priority's lexer and this scan may disagree about where a
// comment is (unmeasured; handoff 2026-10-21-delete-hold-comment-lexer), so a
// body holding one gets no comment exemption.
const DISPUTED_ANYWHERE = ['\\', '"'];
const DISPUTED_OUTSIDE_COMMENTS = ['--', '//'];
const DISPUTED_IN_LITERALS = ['/*', '*/', '\r', '\n'];

// One left-to-right pass (the same scan as devruns._scan): { comments, literals,
// disputed }. comments/literals are [start, end) pairs; a quote opens a literal
// first and closes at the next quote (an unterminated one runs to the end); a
// `/*` with no `*/` after it is not a comment. `disputed` is true when
// Priority's lexer could disagree: the body holds a backslash or double
// quote; a `--` or `//` sits outside a comment (in code or in a literal); a
// literal holds `/*`, `*/` or a line break; or a comment opens as `/*/`.
function scan(s) {
  let disputed = DISPUTED_ANYWHERE.some((t) => s.includes(t));
  const comments = [];
  const literals = [];
  let i = 0;
  while (i < s.length) {
    if (s[i] === "'") {
      let end = i + 1;
      while (end < s.length && s[end] !== "'") end += s[end] === '\\' ? 2 : 1; // \' is an escaped quote
      end = Math.min(end, s.length);
      const lit = s.slice(i, end);
      if ([...DISPUTED_IN_LITERALS, ...DISPUTED_OUTSIDE_COMMENTS].some((t) => lit.includes(t))) disputed = true;
      literals.push([i, Math.min(end + 1, s.length)]);
      i = end + 1;
    } else if (s.startsWith('/*', i)) {
      if (s.startsWith('/*/', i)) disputed = true;
      const close = s.indexOf('*/', i + 2);
      if (close < 0) {
        i += 2;
      } else {
        comments.push([i, close + 2]);
        i = close + 2;
      }
    } else {
      if (DISPUTED_OUTSIDE_COMMENTS.some((t) => s.startsWith(t, i))) disputed = true;
      i += 1;
    }
  }
  return { comments, literals, disputed };
}

// Every DELETE that holds a card, as {start, end, where}: each match that is not
// inside a /* ... */ comment and does not open a generator's own staging
// cleanup (DELETE FROM NCATALOG ... WHERE USER = SQL.USER;), which Tal
// exempted on 2026-09-24. A DELETE inside a string literal still holds, and
// says so: where is 'literal' (stored code that runs later) or 'code'.
export function deleteSites(text) {
  const s = String(text ?? '');
  const { comments, literals, disputed } = scan(s);
  const skip = disputed ? [] : comments;
  const out = [];
  for (const m of s.matchAll(DELETE_RE)) {
    if (skip.some(([a, b]) => a <= m.index && m.index < b)) continue;
    STAGING_RE.lastIndex = m.index;
    if (STAGING_RE.test(s)) continue;
    const where = literals.some(([a, b]) => a <= m.index && m.index < b) ? 'literal' : 'code';
    out.push({ start: m.index, end: m.index + m[0].length, where });
  }
  return out;
}

export const deleteSpans = (text) => deleteSites(text).map(({ start, end }) => [start, end]);

// A text as [{type: 'text'|'delete', text}], for <mark>ing each held DELETE.
export function deleteSegments(text) {
  const s = String(text ?? '');
  const segments = [];
  let at = 0;
  for (const [start, end] of deleteSpans(s)) {
    if (start > at) segments.push({ type: 'text', text: s.slice(at, start) });
    segments.push({ type: 'delete', text: s.slice(start, end) });
    at = end;
  }
  if (at < s.length || segments.length === 0) segments.push({ type: 'text', text: s.slice(at) });
  return segments;
}

// A fragment of the whole body (starting at offset `base`) cut by the whole-text
// `sites`: [{type: 'text'|'delete', text, del}], so each counted site renders a mark
// (one per fragment it touches, all sharing `del`).
export function markSegments(text, base, sites) {
  const s = String(text ?? '');
  const out = [];
  let at = 0;
  sites.forEach(({ start, end }, i) => {
    const a = Math.max(start - base, 0);
    const b = Math.min(end - base, s.length);
    if (b <= a || a < at) return;
    if (a > at) out.push({ type: 'text', text: s.slice(at, a) });
    out.push({ type: 'delete', text: s.slice(a, b), del: i });
    at = b;
  });
  if (at < s.length || out.length === 0) out.push({ type: 'text', text: s.slice(at) });
  return out;
}

// Stepping through a held card's DELETEs, wrapping at both ends; -1 when none.
export function stepDelete(count, index, dir) {
  if (!count) return -1;
  if (index < 0) return dir < 0 ? count - 1 : 0; // nothing selected yet
  return (((index + dir) % count) + count) % count;
}

// Which text the stepper walks: the decoded pane only when it is open and marks a DELETE.
export const stepperSource = ({ paneOpen, paneCount }) => (paneOpen && paneCount > 0 ? 'pane' : 'raw');

export function controlWarning(count) {
  if (!count) return null;
  return `This body contains ${count} invisible direction/format characters — shown as ⟨U+…⟩. `
    + 'What you copy or approve is not what a plain view shows.';
}

export const canCancel = (status) => status === 'queued' || status === 'waiting';
export const canFail = (status) => status === 'running';

// Spec 2026-09-27 Part A: Stop pauses a live run and keeps its session; a
// queued run has nothing to pause (Cancel), a finished one nothing to stop.
export const canStop = (status) => status === 'running' || status === 'waiting';

// operator.reply's own refusals, so the box is never offered on a run the
// route answers 409: a done run, a session to resume, turns left, and (spec
// 2026-09-27-workbench-stage-runs P14) never a stage run -- the server
// refuses a reply to a done stage with 409 reason "stage" regardless of its
// session. A waiting run is answered on its card; a failed run's session is
// not resumed.
export function canReply(run) {
  if (!run || run.status !== 'done' || !run.sdk_session_id || run.kind === 'stage') return false;
  return !(run.turn_budget && run.turns >= run.turn_budget);
}

// Spec 2026-09-25-qa-verify-run §3.9 + 2026-09-27-workbench-stage-runs B.16
// (F14): the route's own refusals -- a done dev run or a done stage (Verify
// on a single stage stays available); a verify run is never verified again.
export const canVerify = (run) => !!run && run.status === 'done'
  && (run.kind === 'dev' || run.kind === 'stage');

// A task's run, a verify run and a review run have no auto mode (the routes answer 409; a
// verify run's approved plan admits its test loads; a review acts on nothing).
export const canAuto = (run) => !!run && run.task_id == null && run.kind !== 'verify'
  && run.kind !== 'review';

// Under this many turns a reply leg can park on a gated answer, have it run on
// the ERP, and fail at max turns before it reports (runner._max_turns).
export const LOW_TURNS = 3;

// {left, low} under a turn budget, null without one.
export function turnsLeft(run) {
  if (!run || !run.turn_budget) return null;
  const left = Math.max(0, run.turn_budget - (run.turns || 0));
  return { left, low: left < LOW_TURNS };
}

// The page's advisory copy of the route's text gate; the server's
// tools._check_text is the gate (and refuses C0/C1 controls too). A code,
// not prose, so the page words it in its own language.
export function replyTextProblem(text) {
  const r = text || '';
  if (!r.trim()) return { code: 'required' };
  if (r.length > REPLY_MAX) return { code: 'too_long', max: REPLY_MAX };
  const bad = r.match(CONTROLS);
  if (bad) return { code: 'control', label: controlLabel(bad[0].codePointAt(0)) };
  return null;
}

export function uploadProblem(file) {
  if (!file) return 'Choose a file.';
  if (file.size > MAX_UPLOAD_BYTES) return 'An upload is at most 20 MB.';
  return null;
}

export function failReasonProblem(reason) {
  const r = reason || '';
  if (!r.trim()) return 'A reason is required.';
  if (r.length > FAIL_REASON_MAX) return `A reason is at most ${FAIL_REASON_MAX} characters.`;
  return null;
}

export function mergeEvents(prev, incoming) {
  const bySeq = new Map();
  for (const e of prev || []) bySeq.set(e.seq, e);
  for (const e of incoming || []) bySeq.set(e.seq, e);
  return [...bySeq.values()].sort((a, b) => a.seq - b.seq);
}

// Approval statuses that settle without anything further acting on them.
// `pending` (still deciding) and `approved`/`auto` (execute_approved or the
// read-back sweep runs on the NEXT leg) and `promoting` are not terminal --
// a run can go `done` (its leg ended) while a job it just enqueued is still
// `pending`/`claimed` on the ERP driver, so the run's own status alone does
// not mean the page has caught up with everything it filed.
const TERMINAL_APPROVAL_STATUSES = new Set(['rejected', 'done', 'failed']);
export function approvalIsTerminal(status) {
  return TERMINAL_APPROVAL_STATUSES.has(status);
}
export function anyApprovalPending(approvals) {
  return (approvals || []).some((a) => !approvalIsTerminal(a && a.status));
}

// While a running run's model writes code, poll fast enough to show it grow
// (the worker writes the draft at most every 250 ms; spec 2026-09-29 §6).
export const LIVE_POLL_MS = 500;

// ms until the next poll, or null once the run is over AND every approval
// on it has settled.
export function pollDelay(status, approvals, live = null) {
  if (status === 'running' && live) return LIVE_POLL_MS;
  if (status === 'queued' || status === 'running') return 2000;
  if (status === 'waiting') return 8000;
  if (anyApprovalPending(approvals)) return 8000;
  return null;
}

// Which of `cards`' ids are new since `seenIds` was last updated. The
// caller owns `seenIds` (a Set persisted across renders/polls) and mutates
// it itself, so this stays a pure read; order follows `cards`, so "the
// first new one" is a deterministic pick for scrolling.
export function newActionableIds(cards, seenIds) {
  const seen = seenIds || new Set();
  return (cards || []).map((a) => a.id).filter((id) => !seen.has(id));
}

// Fail closed before enabling Approve when the complete mail cannot be displayed.
export function mailPreview(body) {
  try {
    const m = JSON.parse(body);
    if (!m || !Array.isArray(m.to) || !m.to.length || !Array.isArray(m.cc)
      || !m.to.concat(m.cc).every(a => typeof a === 'string' && a.trim())
      || typeof m.subject !== 'string' || typeof m.body !== 'string') return null;
    return {to:m.to, cc:m.cc, subject:m.subject, body:m.body};
  } catch (_) { return null; }
}


// A `plan` card's six fields, or null when the body is not one. Same shape
// and the same fail-closed contract as mailPreview: the card renders nothing
// structured it could not parse, and the raw body is shown either way.
// `unknowns` and `questions` come FIRST in the object because they are what
// the operator's time is worth spending on (tasks/develop.md §3).
export function planPreview(body) {
  try {
    const p = JSON.parse(body);
    if (!p || typeof p !== 'object' || Array.isArray(p)) return null;
    if (typeof p.summary !== 'string' || !p.summary.trim()) return null;
    const list = (v) => (Array.isArray(v) ? v.filter((s) => typeof s === 'string') : []);
    return {
      unknowns: list(p.unknowns), questions: list(p.questions),
      summary: p.summary, targets: list(p.targets),
      lanes: list(p.lanes), steps: list(p.steps),
      stages: planStages(body),
      // A verify plan's requests for write interfaces (spec 2026-09-26 part B
      // §5): shown so APPROVE on the page reads what it files as drafts.
      interfaces: (Array.isArray(p.interfaces) ? p.interfaces : [])
        .filter((i) => i && typeof i === 'object' && typeof i.name === 'string')
        .map((i) => `${i.name}: ${(Array.isArray(i.forms) ? i.forms : [])
          .map((f) => (f && typeof f.ename === 'string' ? f.ename : '?')).join(', ')}`),
    };
  } catch (_) { return null; }
}

// The server's own reading of a verify run's report (spec 2026-09-26-qa-verify-part-b
// §4.3), or null for a run it did not check. `title` lists every mismatch, one
// per line -- the per-row detail a stored event would have carried (spec R1).
export function qaChip(qa) {
  if (!qa || typeof qa !== 'object' || typeof qa.verdict !== 'string') return null;
  const mismatches = Array.isArray(qa.mismatches) ? qa.mismatches : [];
  const cls = qa.verdict === 'PASS' ? 'st-ok'
    // A "does not match" chip uses the page's warning class (spec §4.3, controller ruling):
    // EVIDENCE MISMATCH is not danger-red.
    : ['FAIL', 'REPORT INVALID'].includes(qa.verdict) ? 'st-danger' : 'st-warn';
  const lines = mismatches.map((m) => `${m && m.row}: ${m && m.problem}`);
  if (qa.error) lines.push(String(qa.error));
  return { verdict: qa.verdict, count: mismatches.length, cls, title: lines.join('\n') };
}

// The `detail` autodecide writes when it holds a card (autodecide._hold).
// Returns the reason without its prefix, or null -- the card then shows the
// generic Detail block instead, so the text is never displayed twice.
export const HOLD_PREFIX = 'waiting for you: ';
export function holdReason(detail) {
  const s = typeof detail === 'string' ? detail : '';
  return s.startsWith(HOLD_PREFIX) ? s.slice(HOLD_PREFIX.length) : null;
}

// What the Approve button sends as `cost_budget_usd`. An EMPTY box sends
// nothing, and what the server does then depends on the CARD: on a `plan` it
// falls back to `workbench_run_budget_usd`, on any other card it leaves the
// run's own budget alone. So the refusal below says "or blank" and stops
// there -- the kind-aware LABEL (`costBudget` / `costBudgetKeep` in
// workbench.jsx) carries the meaning, and a message naming "the default"
// would be false on the ceiling card this box now also appears on.
// DecisionBody is gt=0 and le=10000, so 0 or a number past the ceiling is a
// 422 -- refused here, with a sentence, rather than sent and rejected far
// from the box. A bad value is never silently turned into the server's
// default: the operator typed a number and would otherwise get a different
// one with nothing said.
export const COST_BUDGET_MAX = 10000;

export function costBudgetProblem(raw) {
  if (raw === '' || raw === null || raw === undefined) return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0 || n > COST_BUDGET_MAX) {
    return `A cost budget is a number above 0 and at most ${COST_BUDGET_MAX}, or blank.`;
  }
  return null;
}

export function costBudgetValue(raw) {
  if (costBudgetProblem(raw) !== null) return null;
  if (raw === '' || raw === null || raw === undefined) return null;
  return Number(raw);
}

// A turn ceiling, the other half of what `runner._ceiling_body` promises.
// DecisionBody is ge=1 and le=100000 and the field is an INT, so a fraction
// is a 422 -- refused here rather than sent. Blank always means "leave the
// run's turn budget as it is": unlike a plan card's cost box there is no
// server-side default to fall back to.
export const TURN_BUDGET_MAX = 100000;

export function turnBudgetProblem(raw) {
  if (raw === '' || raw === null || raw === undefined) return null;
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1 || n > TURN_BUDGET_MAX) {
    return `A turn budget is a whole number from 1 to ${TURN_BUDGET_MAX}, or blank to leave it alone.`;
  }
  return null;
}

export function turnBudgetValue(raw) {
  if (turnBudgetProblem(raw) !== null) return null;
  if (raw === '' || raw === null || raw === undefined) return null;
  return Number(raw);
}

// Which cards offer the two ceiling fields.
//
// THE SERVER ALREADY ACCEPTS BOTH ON ANY APPROVED CARD (`DecisionBody`, and
// `approvals.decide`'s `raise_` block, which is deliberately outside the
// `kind == "plan"` branch). This set is the PAGE's choice of where to offer
// them, and it is load-bearing in one direction: `runner._ceiling_body`
// promises by name that "the Approve dialog carries both fields", and the
// ceiling card is filed as `runner.CEILING_CARD_KIND` -- `stuck`. Drop
// `stuck` from this set and that sentence becomes false, and a run whose
// budget is spent has no escape at all: `_leg_budget` floors the next leg at
// MIN_LEG_BUDGET_USD, it ends at `error_max_budget_usd`, and the operator
// gets the same card again, forever, at one resume leg's tokens each.
// `tests/test_workbench_runner.py` ties the two ends together.
//
// `plan` because Continue is where a run's envelope is set in the first
// place. Not every card: a `run_sqli` card's whole job is that someone reads
// the SQLI before approving it, and a number box beside that text competes
// with the only gate the page has. The case that misses -- a ceiling reached
// while another card was already pending, where `_end_leg` parks on that
// card instead of filing a ceiling one -- converges rather than loops: the
// next leg runs at MIN_LEG_BUDGET_USD, ends on the ceiling with nothing
// pending, and files the card that does carry the fields.
// `dispatch` joins the set spec 2026-09-27-workbench-stage-runs Task 13:
// a dispatch held for the chain budget is approved with a raised one.
export const BUDGET_FIELD_KINDS = new Set(['plan', 'stuck', 'dispatch']);
export const showsBudgetFields = (kind) => BUDGET_FIELD_KINDS.has(kind);

// A plan's stages, as the server stored them (tools._plan_body): [] when none.
export function planStages(body) {
  try {
    const p = JSON.parse(body);
    return Array.isArray(p && p.stages) ? p.stages.filter((s) => s && typeof s === 'object') : [];
  } catch (_) { return []; }
}

// A dispatch card, readable: a planned stage is read from the run's chain plan
// (the latest approved plan card), a fix from the card itself. null = show the body.
export function dispatchPreview(body, planBody) {
  try {
    const card = JSON.parse(body);
    if (!card || typeof card !== 'object') return null;
    const stages = planStages(planBody);
    if (Number.isInteger(card.stage)) {
      const s = stages[card.stage - 1];
      return s ? { ...s, number: card.stage, of: stages.length, fix: false } : null;
    }
    if (card.fix && typeof card.fix === 'object' && Number.isInteger(card.fix_of)) {
      return { ...card.fix, number: card.fix_of, of: stages.length, fix: true };
    }
    return null;
  } catch (_) { return null; }
}

// ---- presenter mode + code cards (2026-09-29) ----

// The tools whose input IS code, and where in the input it sits. Everything
// else stays a collapsed tool row. MCP tools arrive as mcp__wb__<name>
// (app/workbench/sdk_adapter.py _WB_PREFIX); only that prefix is ours.
const SQL_FILE = /\.(q|sql)$/i;
// The worker streams exactly these while they are written (STREAMED_TOOLS in
// app/workbench/livedraft.py; tests/test_workbench_livedraft.py pins the two).
const CODE_TOOLS = {
  Write: (i) => ({ verb: 'write', path: i.file_path ?? null, code: i.content }),
  Edit: (i) => ({ verb: 'edit', path: i.file_path ?? null, code: i.new_string ??
    (Array.isArray(i.edits) ? i.edits.map((e) => e?.newText).filter((v) => typeof v === 'string').join('\n\n') : null) }),
  Bash: (i) => ({ verb: 'shell', path: null, code: i.command, lang: 'text' }),
  // By body_file it carries no code: the Write card before it showed the file.
  run_sqli: (i) => ({ verb: 'sqli', path: null, code: i.body, lang: 'sql' }),
  hand_me_dbi: (i) => ({ verb: 'dbi', path: null, code: i.body, lang: 'sql' }),
  mirror_sql: (i) => ({ verb: 'mirror', path: null, code: i.query, lang: 'sql' }),
  read_back: (i) => ({ verb: 'readback', path: null, code: i.sql, lang: 'sql' }),
};
const MCP_TOOL = /^mcp__wb__(run_sqli|hand_me_dbi|mirror_sql|read_back)$/;

// {verb, path, code, lang} for a tool block that carries code, else null.
export function codeOf(block) {
  if (!block || block.type !== 'tool' || !block.input || typeof block.input !== 'object') return null;
  const name = String(block.name ?? '');
  const m = MCP_TOOL.exec(name);
  const pick = CODE_TOOLS[m ? m[1] : (name.startsWith('mcp__') ? '' : name)];
  if (!pick) return null;
  const c = pick(block.input);
  if (typeof c.code !== 'string' || !c.code) return null;
  return { ...c, lang: c.lang || (SQL_FILE.test(c.path || '') ? 'sql' : 'text') };
}

// ---- live drafts, PR 3 (spec 2026-09-29-workbench-live-stream §6) ----

const JSON_ESC = { n: '\n', t: '\t', r: '\r', '"': '"', '\\': '\\', '/': '/', b: '\b', f: '\f' };
const HEX4 = /^[0-9a-fA-F]{4}$/;
const LOW_SURROGATE = /^\\u[dD][c-fC-F][0-9a-fA-F]{2}$/;

// The JSON string whose opening quote is s[i]: {value, end, done}. `end` is
// the index past the closing quote; done=false when the text ran out first,
// and then `value` is what is complete (an escape cut in half is dropped).
function readJsonString(s, i) {
  let out = '';
  let j = i + 1;
  while (j < s.length) {
    const c = s[j];
    if (c === '"') return { value: out, end: j + 1, done: true };
    if (c !== '\\') { out += c; j += 1; continue; }
    const e = s[j + 1];
    if (e === 'u') {
      const hex = s.slice(j + 2, j + 6);
      if (!HEX4.test(hex)) break;
      const code = parseInt(hex, 16);
      if (code >= 0xd800 && code <= 0xdbff) {
        const lo = s.slice(j + 6, j + 12);
        if (lo.length < 6) break;              // its low half has not arrived
        if (LOW_SURROGATE.test(lo)) {
          out += String.fromCharCode(code, parseInt(lo.slice(2), 16));
          j += 12;
          continue;
        }
      }
      out += String.fromCharCode(code);
      j += 6;
      continue;
    }
    if (e === undefined || !(e in JSON_ESC)) break;
    out += JSON_ESC[e];
    j += 2;
  }
  return { value: out, end: s.length, done: false };
}

// The index past the JSON value starting at s[i], or -1 if the text ends inside it.
function skipJsonValue(s, i) {
  if (s[i] === '"') { const r = readJsonString(s, i); return r.done ? r.end : -1; }
  if (s[i] === '{' || s[i] === '[') {
    let depth = 0;
    for (let j = i; j < s.length; j += 1) {
      const c = s[j];
      if (c === '"') {
        const r = readJsonString(s, j);
        if (!r.done) return -1;
        j = r.end - 1;
      } else if (c === '{' || c === '[') depth += 1;
      else if (c === '}' || c === ']') { depth -= 1; if (depth === 0) return j + 1; }
    }
    return -1;
  }
  let j = i;
  while (j < s.length && !/[\s,}\]]/.test(s[j])) j += 1;
  return j < s.length ? j : -1;
}

// The decoded string value of a TOP-LEVEL key of an unfinished JSON object,
// as far as it has arrived; null when the key has not started or its value
// is not a string. It walks keys and values, so a key name inside a value
// (code containing `"content":`) never matches. Never throws.
export function partialField(partialJson, key, decodeValue = null) {
  const s = typeof partialJson === 'string' ? partialJson : '';
  const ws = (j) => { while (j < s.length && /\s/.test(s[j])) j += 1; return j; };
  let i = ws(0);
  if (s[i] !== '{') return null;
  i += 1;
  for (;;) {
    i = ws(i);
    if (s[i] !== '"') return null;
    const k = readJsonString(s, i);
    if (!k.done) return null;
    i = ws(k.end);
    if (s[i] !== ':') return null;
    i = ws(i + 1);
    if (k.value === key) return decodeValue ? decodeValue(s, i) : s[i] === '"' ? readJsonString(s, i).value : null;
    if (i >= s.length) return null;
    i = skipJsonValue(s, i);
    if (i < 0) return null;
    i = ws(i);
    if (s[i] !== ',') return null;
    i += 1;
  }
}

function partialEditText(s, i) {
  if (s[i] !== '[') return null;
  const parts = [];
  i += 1;
  while (i < s.length) {
    while (i < s.length && /\s/.test(s[i])) i += 1;
    if (s[i] !== '{') break;
    const text = partialField(s.slice(i), 'newText');
    if (text !== null) parts.push(text);
    i = skipJsonValue(s, i);
    if (i < 0) break;
    while (i < s.length && /\s/.test(s[i])) i += 1;
    if (s[i] !== ',') break;
    i += 1;
  }
  return parts.length ? parts.join('\n\n') : null;
}

// Every input field codeOf reads; codeOf then picks the tool's own, so there
// is no second copy of the tool-to-field map.
const DRAFT_FIELDS = ['file_path', 'content', 'new_string', 'command', 'body', 'query', 'sql', 'path', 'newText'];

// The run's live draft as a pseudo tool block, or null: no draft, its real
// tool_use already in `blocks` (the event won the race), or no code yet (only
// file_path has arrived -- a collapsed row that then became a card would jump).
export function draftBlock(live, blocks) {
  if (!live || typeof live !== 'object' || !live.tool_use_id) return null;
  if ((blocks || []).some((b) => b.type === 'tool' && b.id === live.tool_use_id)) return null;
  const input = {};
  for (const f of DRAFT_FIELDS) {
    const v = partialField(live.partial, f);
    if (v !== null) input[f] = v;
  }
  // Pi's streamed file arguments use these aliases; completed events are normalized by its adapter.
  input.file_path ??= input.path;
  input.new_string ??= input.newText;
  input.new_string ??= partialField(live.partial, 'edits', partialEditText);
  const b = { type: 'tool', id: live.tool_use_id, name: live.name ?? null, input, result: null, draft: true };
  return codeOf(b) ? b : null;
}

// Keywords are Priority's own RESERVED table (priority-reserved.mjs, a saved
// snapshot -- never fetched), its UPPERCASE rows: the lowercase ones are the
// backing database's names (part, user, table), which are Priority table and
// column names in code. Plus the clause words RESERVED does not list, which
// SQLI and the mirror's Postgres queries use. A function is one only when
// called, whatever RESERVED says, so the function test comes first.
const SQL_KW_EXTRA = 'AS ON JOIN LEFT RIGHT INNER OUTER FULL CROSS CREATE DROP ALTER TABLE INDEX UNION CASE WHEN '
  + 'THEN ELSE LIMIT OFFSET WITH IF GENMSG MAILMSG INTERFACE FILTER WINDBI PRINT PRINTCONT ONEROW STOP TRUE FALSE';
// Built on first use, not at import: chat.jsx imports this module too, and
// only a function its bundler can drop keeps these words out of chat.js.
let sqlKw = null;
function sqlKeywords() {
  if (!sqlKw) {
    sqlKw = new Set(SQL_KW_EXTRA.split(' '));
    for (const w of PRIORITY_RESERVED) if (w === w.toUpperCase() && /^[A-Z_]\w*$/.test(w)) sqlKw.add(w);
  }
  return sqlKw;
}
// A regex literal for the same reason: its bundler can prove it side-effect free.
const SQL_FN = /^(?:STRCAT|STRIND|STRINDEX|STRLEN|SUBSTR|STRPIECE|RSTRIND|ITOA|ATOI|ATOR|RTOA|DTOA|ATOD|TOUPPER|TOLOWER|ROUND|ROUNDR|MOD|MINOP|MAXOP|ABS|SQRT|POW|EXP|YEAR|MONTH|MDAY|WEEK|WEEK6|QUARTER|BEGINOFMONTH|ENDOFMONTH|BEGINOFYEAR|ENDOFYEAR|BEGINOFQUARTER|ENDOFQUARTER|NEWATTACH|ENTMESSAGE|SUM|COUNT|MIN|MAX|AVG|REALQUANT|INTQUANT|FIXEDQUANT|SYSPATH|COALESCE|LOWER|UPPER|NOW|CAST|TRIM|LENGTH)$/i;
// comment | line comment | string ('' and \' both escape) | variable | word |
// number | a "quoted identifier" (a mirror query's), consumed and left plain.
// An unterminated comment or string runs to its end: what a card shows mid-type.
const SQL_RE = /(\/\*[\s\S]*?(?:\*\/|$))|(--[^\n]*)|('(?:[^'\\\n]|\\.|'')*'?)|((?<!:):[$\w.@]*[\w@$]|\bSQL\.\w+)|(#INCLUDE\b|\b[A-Za-z_]\w*)|(\b\d+(?:\.\d+)?\b)|"[^"\n]*"?/g;

// [{cls, start, end}] over the WHOLE text, coloured ranges only. Tokenised once
// so a DELETE mark or a control chip cut inside a comment stays comment-coloured.
export function sqlTokens(text) {
  const s = String(text ?? '');
  const out = [];
  for (const m of s.matchAll(SQL_RE)) {
    const [w, com, line, str, v, word, num] = m;
    let cls = null;
    if (com || line) cls = 'com';
    else if (str) cls = 'str';
    else if (v) cls = 'var';
    else if (word) {
      const u = word.toUpperCase();
      if (SQL_FN.test(u) && /^\s*\(/.test(s.slice(m.index + w.length))) cls = 'fn';
      else if (u === '#INCLUDE' || sqlKeywords().has(u)) cls = 'kw';
    } else if (num) cls = 'num';
    if (cls) out.push({ cls, start: m.index, end: m.index + w.length });
  }
  return out;
}

// text.slice(from, to) as [{cls, text}], split at the token boundaries.
export function sqlSlices(text, tokens, from, to) {
  const out = [];
  let at = from;
  for (const k of tokens) {
    if (k.end <= at) continue;
    if (k.start >= to) break;
    if (k.start > at) out.push({ cls: null, text: text.slice(at, k.start) });
    const end = Math.min(k.end, to);
    out.push({ cls: k.cls, text: text.slice(Math.max(k.start, at), end) });
    at = end;
  }
  if (at < to) out.push({ cls: null, text: text.slice(at, to) });
  return out;
}

// An approval whose body is SQL. A job's body is its JSON payload; its SQL is
// the rendered preview, highlighted where it is drawn.
export const sqlBodyKind = (kind) => kind === 'run_sqli' || kind === 'hand_me_dbi';

// The decoded loads a run_sqli preview carries (app/workbench/cardcode.py).
// The decoded pane opens when at least one load is code, not a bare 'other'.
// The decoder's exact words for a load that is plain data, not a failure (cardcode.py).
export const NOT_CODE_REASON = 'not a code interface';
const isPlainData = (l) => Boolean(l) && l.kind === 'other' && l.reason === NOT_CODE_REASON;

// A load that failed for any other reason still opens the pane, so its notice is seen.
export const hasCodeView = (code) =>
  Array.isArray(code) && code.some((l) => l && l.kind !== 'other' ? true : Boolean(l && l.reason && !isPlainData(l)));

// The line under a load the decoder could not read: its interface, the reason, and where to look.
export const codeNotDecodedText = (load, t) =>
  t.codeNotDecoded((load && load.interface) || '?', (load && load.reason) || '');

// The notice under a load that did not decode; '' for code, a delete, and plain data.
export const codeNotice = (load, t) =>
  load && load.state === 'not_decoded' && !isPlainData(load) ? codeNotDecodedText(load, t) : '';

// The parser's findings for the trigger text a run_sqli card loads.
export const syntaxWarningLines = (approval) =>
  Array.isArray(approval && approval.syntax_warnings) ? approval.syntax_warnings.filter(Boolean) : [];

// A load's heading in plain words. A field the decoder did not read is '?'.
export function codeHeading(load, t) {
  const l = load || {};
  const q = (v) => (v === null || v === undefined || v === '' ? '?' : v);
  if (l.state === 'delete') {
    const what = {
      form_trigger: () => t.codeWhatTrigger(q(l.trigger), q(l.form)),
      column_trigger: () => t.codeWhatColumnTrigger(q(l.trigger), q(l.form), q(l.column)),
      proc_step: () => t.codeWhatStep(q(l.procedure), q(l.pos)),
      step_text: () => t.codeWhatStep(q(l.procedure), q(l.pos)),
      trigger_name: () => t.codeWhatTriggerName(q(l.trigger)),
    }[l.kind];
    const w = what ? what() : q(l.interface);
    return l.deletes === 'lines' ? t.codeDeleteLines(w) : t.codeDeleteObject(w);
  }
  switch (l.kind) {
    case 'form_trigger': return t.codeFormTrigger(q(l.trigger), q(l.form));
    case 'column_trigger': return t.codeColumnTrigger(q(l.trigger), q(l.form), q(l.column));
    case 'proc_step': return t.codeStep(q(l.procedure), q(l.pos), q(l.program));
    case 'step_text': return t.codeStepText(q(l.procedure), q(l.pos));
    case 'trigger_name': return t.codeTriggerName(q(l.trigger));
    default: return isPlainData(l) ? t.codeOther(q(l.interface)) : t.codeUnknownLoad(q(l.interface));
  }
}

// Presenter mode follows the agent unless a card waits (the page stays on it)
// or someone touched the mouse/keyboard in the last FOLLOW_PAUSE_MS. `force`
// is the hand-back after a decision, whose own click would otherwise pause it.
export const FOLLOW_PAUSE_MS = 10000;
export const followDue = ({ on, waiting, idleMs, want, force = false }) =>
  Boolean(on && !waiting && (force || (want && idleMs >= FOLLOW_PAUSE_MS)));

// How many lines of a typing code card show after elapsedMs: 60 ms a line,
// but never longer than TYPE_MAX_MS for the whole card.
export const TYPE_MAX_MS = 3000;
export function revealedLines(total, elapsedMs) {
  if (!total) return 0;
  const perLine = Math.min(60, TYPE_MAX_MS / total);
  return Math.min(total, Math.floor(elapsedMs / perLine) + 1);
}

export function firstLines(text, n) {
  if (n <= 0) return '';
  let at = -1;
  for (let i = 0; i < n; i += 1) {
    at = text.indexOf('\n', at + 1);
    if (at < 0) return text;
  }
  return text.slice(0, at);
}

// The page is laid out for PRESENTER_W css px and zoomed to the window, so a
// laptop and a 4K TV show the same layout. Floored so a small window stays readable.
export const PRESENTER_W = 1440;
export const presenterZoom = (width) => Math.max(0.5, width / PRESENTER_W);

// ---- presenter mode, PR 2: the now-doing strip (2026-09-29) ----

// A tool's own name: the workbench MCP server's prefix stripped, another
// server's tool is nobody's (codeOf's rule).
const WB_PREFIX = 'mcp__wb__';
const toolBase = (name) => {
  const n = String(name ?? '');
  if (n.startsWith(WB_PREFIX)) return n.slice(WB_PREFIX.length);
  return n.startsWith('mcp__') ? '' : n;
};
const DOING = {
  Write: 'write', Edit: 'write', run_sqli: 'sqli', enqueue_write_job: 'job', mirror_sql: 'mirror',
  Read: 'read', Grep: 'read', Glob: 'read', code_search: 'read', plan: 'plan',
};

// Queued/terminal/Stop state wins over cards retained during the decision hold.
export function nowDoing(blocks, run, waiting) {
  const state = workbenchDisplayState(run);
  if (['queued', 'done', 'failed', 'cancelled', 'stopped', 'stop_requested'].includes(state)) return { key: 'status', arg: state };
  if (waiting) return { key: 'approval', arg: null };
  if (run && run.status !== 'running') return { key: 'status', arg: run.status };
  const last = blocks && blocks.length ? blocks[blocks.length - 1] : null;
  if (last && last.type === 'text') return { key: 'thinking', arg: null };
  if (!last || last.type !== 'tool') return { key: 'working', arg: null };
  const key = DOING[toolBase(last.name)];
  if (!key) return { key: 'working', arg: null };
  const path = key === 'write' && last.input && typeof last.input.file_path === 'string' ? last.input.file_path : '';
  return { key, arg: path ? path.split('/').pop() : null };
}

export function lineCount(text) {
  if (typeof text !== 'string' || !text) return 0;
  const n = text.split('\n').length;
  return text.endsWith('\n') ? n - 1 : n;
}

// SQL runs count calls; jobs sent count actual job ids. Generated SQL lives
// in approval bodies when the call carries only body_file.
const WRITTEN = { Write: 'content', Edit: 'new_string', run_sqli: 'body', hand_me_dbi: 'body' };
export function runCounters(blocks, approvals) {
  const out = { lines: 0, sqlRuns: 0, jobs: 0 };
  const written = new Set();
  const jobs = new Set();
  for (const b of blocks || []) {
    if (b.type !== 'tool') continue;
    const name = toolBase(b.name);
    const input = b.input && typeof b.input === 'object' ? b.input : {};
    if (WRITTEN[name]) {
      const bodies = name === 'Edit' && Array.isArray(input.edits)
        ? input.edits.map((e) => e?.newText) : [input[WRITTEN[name]]];
      for (const body of bodies) {
        out.lines += lineCount(body);
        if (typeof body === 'string') written.add(body);
      }
    }
    if (name === 'run_sqli') out.sqlRuns += 1;
  }
  for (const a of approvals || []) {
    if (a.kind === 'run_sqli' || a.kind === 'hand_me_dbi') {
      if (!written.has(a.body)) {
        out.lines += lineCount(a.body);
        written.add(a.body);
      }
    }
    if ((a.kind === 'run_sqli' || a.kind === 'enqueue_write_job') && a.job_id != null) jobs.add(a.job_id);
  }
  out.jobs = jobs.size;
  return out;
}

// m:ss or h:mm:ss from started_at to finished_at (or now); null before a start.
export function elapsedText(run, now) {
  const start = Date.parse(run && run.started_at);
  if (!Number.isFinite(start)) return null;
  const end = Date.parse(run.finished_at);
  const s = Math.max(0, Math.floor(((Number.isFinite(end) ? end : now) - start) / 1000));
  const two = (n) => String(n).padStart(2, '0');
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h ? `${h}:${two(m)}:${two(s % 60)}` : `${m}:${two(s % 60)}`;
}

// A presenter clicker sends PageDown/PageUp (some ArrowRight/ArrowLeft) and a
// "slideshow" F5 that would reload the page mid-demo. 'agent' = back to the
// agent now, 'card' = show the waiting card. No key is ever a decision:
// a run_sqli or a job acts on the real ERP.
const CLICKER = { PageDown: 'agent', ArrowRight: 'agent', PageUp: 'card', ArrowLeft: 'card' };
export function clickerAction({ key, editable = false, altKey = false, ctrlKey = false, metaKey = false }) {
  if (altKey || ctrlKey || metaKey) return null;   // a person's own shortcut, e.g. Ctrl+F5
  if (key === 'F5') return 'swallow';
  if (editable) return null;
  return CLICKER[key] || null;
}

// One Files-tab row (spec 2026-10-14-offline-installations §3.7): the environment the
// file is for, its version (the first 8 hex of its sha256), and whether it may be
// downloaded. A refused file says why (the server's live verdict) and offers no download.
export function runFileView(f) {
  const refused = typeof f?.refused_reason === 'string' && f.refused_reason ? f.refused_reason : null;
  return {
    target: typeof f?.target_env === 'string' ? f.target_env : '',
    version: typeof f?.sha256 === 'string' ? f.sha256.slice(0, 8) : '',
    refused,
    canDownload: refused === null,
  };
}

// The run's decided run_sqli cards as downloadable files (id order): card-<id>.q holds the stored body.
export function cardFiles(approvals) {
  return (approvals || [])
    .filter((a) => a.kind === 'run_sqli' && a.status !== 'pending')
    .sort((x, y) => x.id - y.id)
    .map((a) => ({ id: a.id, filename: `card-${a.id}.q`, title: a.title, status: a.status, decidedBy: a.decided_by }));
}

// Who decided an approval, in plain words. `system:auto:mcp:` is matched before `system:auto:`.
export function decidedByText(decidedBy, t, actor) {
  if (actor) {
    const raw = actor.id || decidedBy;
    if (actor.kind === 'human' && actor.name) return t.byHuman(actor.name);
    if (actor.kind === 'automatic') return `${t.byAutomatic}${raw ? ` (${raw})` : ''}`;
    if (actor.kind === 'system') return `${t.bySystem}${raw ? ` (${raw})` : ''}`;
    return raw || t.byUnknown;
  }
  if (!decidedBy) return '';
  const s = String(decidedBy);
  let m;
  if (s === 'secret') return t.byAdmin;
  if ((m = /^user:(.+)$/.exec(s))) return t.byUser(m[1]);
  if ((m = /^system:auto:mcp:(?:user:)?(.+)$/.exec(s))) return t.byMcp(m[1]);
  if (s === 'system:auto:trusted') return t.byTrusted;
  if ((m = /^system:auto:(.+)$/.exec(s))) return t.byAuto(m[1]);
  if (s === 'system:policy') return t.byPolicy;
  return s;
}

export function workbenchDisplayState(run, approvals = []) {
  const status = run?.status;
  if (!status || ['done', 'failed', 'cancelled'].includes(status)) return status || '';
  const control = run.controller_control;
  const stopped = run.stop?.pending || approvals.some(a => a.status === 'pending' && isStopCard(a));
  if (stopped || control?.mode === 'stopped') {
    if (status !== 'waiting' || (control && control.status !== 'applied' && (control.mode === 'stopped' || control.child_run_id != null))) return 'stop_requested';
    return stopped ? 'stopped' : 'stop_requested';
  }
  return status === 'waiting' && (run.approval_pending || approvals.some(a => a.status === 'pending')) ? 'waiting_approval' : status;
}

export function absoluteRunTime(value, timezone, locale, {seconds = true} = {}) {
  if (typeof value !== 'string' || !value.trim()) return null;
  const utc = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value) ? value : `${value}Z`;
  const date = new Date(utc);
  if (Number.isNaN(date.getTime())) return null;
  const options = {timeZone:timezone || 'UTC', year:'numeric', month:'2-digit', day:'2-digit',
    hour:'2-digit', minute:'2-digit', ...(seconds ? {second:'2-digit'} : {}), hourCycle:'h23'};
  try { return new Intl.DateTimeFormat(locale, options).format(date); }
  catch { return new Intl.DateTimeFormat(locale, {...options,timeZone:'UTC'}).format(date); }
}

// A card the Stop sweep rejected (runs.STOP_SWEEP_ERROR): a withdrawal, not a person's decision.
export const STOP_SWEEP_ERROR = 'stopped by the operator';
export const isStopWithdrawn = (a) => a.status === 'rejected' && !a.decided_by && !a.decided_at && a.error === STOP_SWEEP_ERROR;

export function approvalOutcome(a, t) {
  if (a.status === 'rejected' && !a.decided_by && !a.decided_at) {
    const closures = {[STOP_SWEEP_ERROR]:t.auditWithdrawn,'run cancelled':t.auditCancelled,'run failed':t.auditFailed};
    if (Object.hasOwn(closures, a.error)) return closures[a.error];
  }
  if (isStopCard(a) && ['approved','rejected'].includes(a.status) && (a.decided_by || a.decided_at)) return t.auditContinued;
  if (a.status === 'rejected') return t.auditRejected;
  if (a.status === 'auto' || a.decided_by?.startsWith('system:auto:')) return t.auditAutomatic;
  return ['approved','done','failed','promoting'].includes(a.status) ? t.auditApproved : a.status;
}
// A refresh replaces all loaded pages together, using only its own fresh keysets.
// Selection belongs to React; invalidating the list never closes the open run.
export function createRunListLoader(fetchPage) {
  let generation = 0, active = null, current = null, disposed = false;
  const reset = () => {
    generation += 1;
    active?.abort();
    active = null;
    current = null;
  };
  const load = async ({filters, pages = 1, append = false}) => {
    if (disposed || active) return null;
    if (append && current && !current.has_more) return current;
    const controller = new AbortController(), started = generation;
    active = controller;
    const previous = append ? current : null;
    let cursor = previous?.next_before_id, result = previous;
    let items = previous?.items || [], count = previous?.pages || 0;
    try {
      for (let page = 0; page < pages; page += 1) {
        result = await fetchPage({...filters, ...(cursor != null ? {before_id:cursor} : {}), signal:controller.signal});
        if (disposed || started !== generation) return null;
        items = [...items, ...(result.items || [])];
        count += 1;
        cursor = result.next_before_id;
        if (!result.has_more || cursor == null) break;
      }
      current = {...result, items:[...new Map(items.map(run => [run.id, run])).values()], pages:count};
      return current;
    } catch (error) {
      if (disposed || started !== generation) return null;
      throw error;
    } finally {
      if (active === controller) active = null;
    }
  };
  return {load, reset, dispose:() => { disposed = true; reset(); }};
}
